import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Reveal } from "./Reveal";
import { GALLERY } from "@/data/content";

export default function Gallery() {
  const [selected, setSelected] = useState(null);

  const prev = () => setSelected((s) => (s - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setSelected((s) => (s + 1) % GALLERY.length);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-[#101C3E]/30 border-t border-[#d4af37]/15">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-6">
              Gallery
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight text-slate-50 max-w-xl">
              Moments from <span className="italic text-[#d4af37]">our journeys.</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-sm leading-relaxed">
            Glimpses of the places we take you — tap any photo to view it full screen.
          </p>
        </Reveal>

        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:balance]">
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.caption}
              data-testid={`gallery-item-${i}`}
              onClick={() => setSelected(i)}
              className="group relative block w-full mb-4 overflow-hidden break-inside-avoid"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
            >
              <img
                src={g.image}
                alt={g.caption}
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="font-display text-lg text-slate-50 text-left">{g.caption}</span>
                <Expand size={16} className="text-[#d4af37] shrink-0" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#0A1128]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setSelected(null)}
              aria-label="Close"
              className="absolute top-6 right-6 w-12 h-12 border border-[#d4af37]/50 text-[#d4af37] flex items-center justify-center hover:bg-[#d4af37] hover:text-[#0A1128] transition-colors"
            >
              <X size={20} />
            </button>
            <button
              data-testid="gallery-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous photo"
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/50 text-[#d4af37] flex items-center justify-center hover:bg-[#d4af37] hover:text-[#0A1128] transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.figure
              key={selected}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[selected].image}
                alt={GALLERY[selected].caption}
                className="w-full max-h-[75vh] object-contain border border-[#d4af37]/30"
              />
              <figcaption className="mt-4 text-center font-display text-xl text-[#d4af37]">
                {GALLERY[selected].caption}
              </figcaption>
            </motion.figure>
            <button
              data-testid="gallery-lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next photo"
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/50 text-[#d4af37] flex items-center justify-center hover:bg-[#d4af37] hover:text-[#0A1128] transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
